// Swiper comes from the shared lib/swiper.min.js bundle (CSS: lib/swiper.min.css), which the templates
// include next to this file. It used to be bundled in here as well - a second copy of Swiper on every
// page that also had a timeline.
import { whenSwiper } from './lib/whenSwiper.js'

const toBool = (v) => v === true || v === 'true' || v === 1 || v === '1'

const heroSliderInit = () => {
    Alpine.data('HeroSlider', () => ({
        swiper: null,
        swiperPending: false,
        pagination: false,
        arrows: false,
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: false,
        loop: true,
        autoplay: 0, // number in ms, or 0/false to disable

        doSettings(pagination, arrows, autoplay, slidesPerView = 1, spaceBetween = 0) {
            this.pagination = toBool(pagination)
            this.arrows = toBool(arrows)
            this.autoplay = parseInt(autoplay) > 0 ? parseInt(autoplay) : 0;
            this.slidesPerView = parseInt(slidesPerView) > 1 ? parseInt(slidesPerView) : 1;
            this.spaceBetween = spaceBetween ?? 0;

            if (this.swiper) {
                if (this.autoplay > 0) {
                    this.swiper.params.autoplay = {
                        delay: this.autoplay,
                        disableOnInteraction: false,
                    }
                    this.swiper.autoplay.start()
                } else {
                    this.swiper.autoplay?.stop()
                }
                this.swiper.update()
            }
        },

        init() {
            this.$nextTick(() => {
                this.initSwiper()
                this.setupHashNavigation()
            })
        },

        /**
         * Find slide index from URL hash - checks if hash matches slide ID or element inside slide
         */
        getSlideIndexFromHash(hash) {
            if (!hash || hash.length < 2) return -1
            if (!this.$refs?.swiper) return -1

            const targetId = hash.startsWith('#') ? hash.slice(1) : hash
            const slides = this.$refs.swiper.querySelectorAll('.swiper-slide')

            for (let i = 0; i < slides.length; i++) {
                const slide = slides[i]
                // Check if hash matches slide ID directly
                if (slide.id === targetId) {
                    return i
                }
                // Check if hash matches element inside slide
                try {
                    const element = slide.querySelector(`#${CSS.escape(targetId)}`)
                    if (element) {
                        return i
                    }
                } catch (e) {
                    // Invalid selector, skip
                }
            }

            return -1
        },

        /**
         * Navigate to slide by hash and scroll slider into view
         */
        navigateToHash(hash) {
            if (!this.swiper) return false

            const slideIndex = this.getSlideIndexFromHash(hash)
            if (slideIndex === -1) return false

            // Navigate to slide (use slideToLoop for loop mode)
            if (this.swiper.params.loop) {
                this.swiper.slideToLoop(slideIndex, 300)
            } else {
                this.swiper.slideTo(slideIndex, 300)
            }

            // Scroll the slider into view
            this.$nextTick(() => {
                this.$refs.swiper?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            })

            return true
        },

        /**
         * Setup hash navigation - listen for hashchange and handle initial hash
         */
        setupHashNavigation() {
            // The hash the page was opened with is handled at the end of createSwiper(), once the
            // slider exists - Swiper may arrive after this runs.

            // Listen for hash changes (when user clicks anchor links)
            window.addEventListener('hashchange', () => {
                this.navigateToHash(window.location.hash)
            })

            // Listen for click events on anchor links targeting elements in this slider
            document.addEventListener('click', (e) => {
                const link = e.target.closest('a[href^="#"]')
                if (!link) return

                const hash = link.getAttribute('href')
                if (!hash || hash === '#') return

                const slideIndex = this.getSlideIndexFromHash(hash)
                if (slideIndex === -1) return

                // Prevent default scroll behavior - we'll handle it
                e.preventDefault()

                // Update URL hash
                history.pushState(null, '', hash)

                // Navigate to the slide
                this.navigateToHash(hash)
            })
        },

        initSwiper() {
            if (!this.$refs?.swiper) return
            if (this.swiper || this.swiperPending) return // prevent re-init

            // Scripts load async: wait for the shared Swiper bundle instead of assuming it is there.
            this.swiperPending = true
            whenSwiper((Swiper, swiperModules) => {
                this.swiperPending = false
                this.createSwiper(Swiper, swiperModules)
            })
        },

        createSwiper(Swiper, swiperModules) {
            if (this.swiper) return

            const root = this.$refs.swiper
            const hasPagination = toBool(this.pagination)
            const hasArrows = toBool(this.arrows)
            const hasAutoplay = parseInt(this.autoplay) > 0

            // Only what this slider uses - lib/swiper.js registers nothing globally.
            const modules = []
            if (hasPagination) modules.push(swiperModules.Pagination)
            if (hasArrows) modules.push(swiperModules.Navigation)
            if (hasAutoplay) modules.push(swiperModules.Autoplay)

            let config = {
                modules,
                observer: true,
                observeParents: true,
                loop: toBool(this.loop),
                autoHeight: this.autoHeight,
                slidesPerView: 1,     // base value
                spaceBetween: parseInt(this.spaceBetween),
                breakpoints: {},      // prepare object for breakpoints
                ...(hasPagination && {
                    pagination: {
                        el: root.querySelector('.swiper-pagination'),
                        clickable: true,
                    },
                }),
                ...(hasArrows && {
                    navigation: {
                        nextEl: this.$root.querySelector('.swiper-button-next'),
                        prevEl: this.$root.querySelector('.swiper-button-prev'),
                    },
                }),
                ...(hasAutoplay && {
                    autoplay: {
                        delay: this.autoplay,
                        disableOnInteraction: false,
                    },
                }),
            }

            if (this.slidesPerView > 1) {
                const bp = config.breakpoints

                switch (this.slidesPerView) {
                    case 2:
                        // mobile still 1, from 1024 show 2
                        bp[1024] = { slidesPerView: 2 }
                        break

                    case 3:
                        // 1 / 2 / 3
                        bp[768] = { slidesPerView: 2 }
                        bp[1024] = { slidesPerView: 3 }
                        break

                    case 4:
                        // 1 / 2 / 3 / 4
                        bp[640] = { slidesPerView: 2 }
                        bp[768] = { slidesPerView: 3 }
                        bp[1024] = { slidesPerView: 4 }
                        break

                    case 5:
                        // 1 / 2 / 3 / 4 / 5
                        bp[640] = { slidesPerView: 2 }
                        bp[768] = { slidesPerView: 3 }
                        bp[1024] = { slidesPerView: 4 }
                        bp[1280] = { slidesPerView: 5 }
                        break

                    case 6:
                        // 2 / 3 / 4 / 5 / 6
                        bp[0]    = { slidesPerView: 2 }
                        bp[640]  = { slidesPerView: 3 }
                        bp[768]  = { slidesPerView: 4 }
                        bp[1024] = { slidesPerView: 5 }
                        bp[1280] = { slidesPerView: 6 }
                        break

                    case 7:
                        // 2 / 4 / 5 / 6 / 7
                        bp[0]    = { slidesPerView: 2 }
                        bp[640]  = { slidesPerView: 4 }
                        bp[768]  = { slidesPerView: 5 }
                        bp[1024] = { slidesPerView: 6 }
                        bp[1280] = { slidesPerView: 7 }
                        break

                    case 8:
                        // 2 / 5 / 6 / 7 / 8
                        bp[0]    = { slidesPerView: 2 }
                        bp[640]  = { slidesPerView: 5 }
                        bp[768]  = { slidesPerView: 6 }
                        bp[1024] = { slidesPerView: 7 }
                        bp[1280] = { slidesPerView: 8 }
                        break
                }
            }

            // Swiper needs more slides than it shows at once for loop mode and warns
            // otherwise; with too few slides the slider simply doesn't loop.
            if (config.loop) {
                const visibleMax = Math.max(
                    config.slidesPerView,
                    ...Object.values(config.breakpoints).map((b) => b.slidesPerView)
                )
                config.loop = root.querySelectorAll('.swiper-slide').length > visibleMax
            }

            this.swiper = new Swiper(root, config)

            // Auto-update when the slider becomes visible (e.g., inside hidden tab)
            const io = new IntersectionObserver((entries) => {
                if (entries.some((e) => e.isIntersecting)) this.swiper.update()
            }, { threshold: 0.01 })
            io.observe(root)

            // Opened with #some-slide in the URL: go there now that the slider exists.
            if (window.location.hash) {
                setTimeout(() => this.navigateToHash(window.location.hash), 100)
            }
        },
    }))
}

document.addEventListener('alpine:init', () => {
    heroSliderInit()
})

// window.HdSwiper / window.HdSwiperModules are set by lib/swiper.js now.
